import { useEffect, useState } from "react"
import { apiFetch } from "../../../utils/apiFetch"

export const SCHEDULE_AVAILABILITY_UPDATED_EVENT =
  "odontoflow:schedule-availability-updated"

export const DAY_KEYS = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
]

export function getDefaultScheduleState() {
  return {
    hours: {
      monday: { enabled: true, start: "09:00", end: "18:00" },
      tuesday: { enabled: true, start: "09:00", end: "18:00" },
      wednesday: { enabled: true, start: "09:00", end: "18:00" },
      thursday: { enabled: true, start: "09:00", end: "18:00" },
      friday: { enabled: true, start: "09:00", end: "18:00" },
      saturday: { enabled: true, start: "09:00", end: "14:00" },
      sunday: { enabled: false, start: "09:00", end: "14:00" },
    },
    breaks: {
      lunchEnabled: false,
      lunchStart: "14:00",
      lunchEnd: "15:00",
      blockedDatesText: "",
      notes: "",
    },
    availability: {
      slotMinutes: 30,
    },
    rules: {
      defaultAppointmentMinutes: 30,
      emergencyBufferMinutes: 15,
      allowBackToBackLongProcedures: false,
      requireTreatmentBeforeConfirm: false,
      autoBlockLunchWhenOpen: false,
      warnWhenDoctorExceeds8Hours: true,
      notes: "",
    },
    meta: {},
  }
}

export function getScheduleStorageKey(clinicId) {
  return `odontoflow_schedule_settings_${clinicId || "default"}`
}

export function parseStoredConfig(rawValue) {
  try {
    const parsed = JSON.parse(rawValue)
    return parsed && typeof parsed === "object" ? parsed : null
  } catch {
    return null
  }
}

export function deepMerge(base, patch) {
  if (!patch || typeof patch !== "object" || Array.isArray(patch)) return base

  const result = { ...base }

  Object.keys(patch).forEach((key) => {
    const baseValue = base?.[key]
    const patchValue = patch?.[key]

    if (
      baseValue &&
      typeof baseValue === "object" &&
      !Array.isArray(baseValue) &&
      patchValue &&
      typeof patchValue === "object" &&
      !Array.isArray(patchValue)
    ) {
      result[key] = deepMerge(baseValue, patchValue)
    } else {
      result[key] = patchValue
    }
  })

  return result
}

export function normalizeScheduleAvailabilityPayload(payload = {}) {
  const normalized = deepMerge(getDefaultScheduleState(), {
    hours: payload.hours,
    breaks: payload.breaks,
    availability: payload.availability,
    rules: payload.rules,
    meta: payload.meta,
  })

  return normalized
}

export function saveScheduleConfigToLocal(clinicId, config, meta = {}) {
  if (typeof window === "undefined") return

  const payload = {
    ...config,
    meta: {
      ...(config?.meta || {}),
      ...meta,
    },
  }

  localStorage.setItem(getScheduleStorageKey(clinicId), JSON.stringify(payload))
}

export function loadLocalScheduleConfig(clinicId) {
  if (typeof window === "undefined") {
    return {
      config: getDefaultScheduleState(),
      source: "default",
      loading: false,
      error: "",
      updatedAt: null,
    }
  }

  const stored = parseStoredConfig(
    localStorage.getItem(getScheduleStorageKey(clinicId)),
  )

  const config = normalizeScheduleAvailabilityPayload(stored || {})

  return {
    config,
    source: stored?.meta?.source || (stored?.meta?.savedAt ? "local" : "default"),
    loading: false,
    error: "",
    updatedAt: stored?.meta?.savedAt || null,
  }
}

export async function fetchScheduleAvailabilityConfig(clinicId) {
  if (!clinicId || clinicId === "default" || clinicId === "ALL") {
    return loadLocalScheduleConfig(clinicId)
  }

  try {
    const params = new URLSearchParams({
      clinicId: String(clinicId),
    })

    const res = await apiFetch(`/api/schedule-availability?${params.toString()}`)
    const data = await res.json().catch(() => null)

    if (!res.ok) {
      throw new Error(
        data?.message || "No se pudo cargar disponibilidad desde backend."
      )
    }

    const config = normalizeScheduleAvailabilityPayload({
      hours: data?.hours,
      breaks: data?.breaks,
      availability: data?.availability,
      rules: data?.rules,
      meta: {
        ...(data?.meta || {}),
        savedAt: data?.updatedAt || data?.meta?.savedAt || "",
        source: data?.source || "backend",
      },
    })

    const updatedAt = data?.updatedAt || data?.meta?.savedAt || ""

    saveScheduleConfigToLocal(clinicId, config, {
      clinicId,
      savedAt: updatedAt,
      source: data?.source || "backend",
    })

    return {
      config,
      source: data?.source || "backend",
      loading: false,
      error: "",
      updatedAt,
      clinicName: data?.clinicName || "",
    }
  } catch (err) {
    const fallback = loadLocalScheduleConfig(clinicId)

    return {
      ...fallback,
      source: fallback.source === "default" ? "fallback" : fallback.source,
      error:
        err?.message ||
        "No se pudo cargar disponibilidad desde backend.",
    }
  }
}

export function useScheduleAvailabilityConfig(clinicId) {
  const [state, setState] = useState(() => ({
    ...loadLocalScheduleConfig(clinicId),
    loading: true,
  }))

  useEffect(() => {
    let cancelled = false

    async function loadConfig() {
      setState((prev) => ({
        ...prev,
        ...loadLocalScheduleConfig(clinicId),
        loading: true,
      }))

      const nextState = await fetchScheduleAvailabilityConfig(clinicId)

      if (!cancelled) {
        setState(nextState)
      }
    }

    loadConfig()

    const handleExternalUpdate = () => {
      loadConfig()
    }

    if (typeof window !== "undefined") {
      window.addEventListener(
        SCHEDULE_AVAILABILITY_UPDATED_EVENT,
        handleExternalUpdate,
      )
    }

    return () => {
      cancelled = true

      if (typeof window !== "undefined") {
        window.removeEventListener(
          SCHEDULE_AVAILABILITY_UPDATED_EVENT,
          handleExternalUpdate,
        )
      }
    }
  }, [clinicId])

  return state
}